/**
 * 订单详情页：接口参数映射 + 展示配置。
 * 接口联调后只需调整 bodyKey / prop 路径或增删配置项，无需改模板结构。
 */

export interface OrderDetailApiParamItem {
  /** 路由 query 上的参数名 */
  queryKey: string
  /** 请求体字段名（与后端契约一致） */
  bodyKey: string
}

/** 详情接口请求参数：从路由 query 组装 POST body */
export const orderDetailApiParamConfig: OrderDetailApiParamItem[] = [{ queryKey: 'orderNo', bodyKey: 'orderNo' }]

export interface OrderDetailFieldItem {
  label: string
  /** 相对详情根对象的路径，支持 a.b.c */
  prop: string
  /** 栅格列数 1-24，默认 8（一行 3 个） */
  span?: number
  copyable?: boolean
  /** 将原始值格式化为可读文案（如操作系统编码 → 安卓/苹果） */
  displayFormat?: 'os'
}

export interface OrderDetailLinkRowItem {
  label: string
  prop: string
  copyable?: boolean
}

export interface OrderDetailProgressStepItem {
  key: string
  label: string
  /** 该步骤完成/展示时间字段，相对根对象 */
  timeProp: string
}

export interface OrderDetailSummaryMetricItem {
  label: string
  prop: string
  /** 与 prop 在同一格展示时置于前面，如币种 + 金额 */
  currencyProp?: string
  /** 将原始值格式化为可读文案（如商品类型 value → label） */
  displayFormat?: 'prodType'
  /** 摘要格内单行省略 + 复制图标（与详情区 field.copyable 行为一致） */
  copyable?: boolean
}

export interface OrderDetailSectionActionItem {
  label: string
  /** 点击后在弹窗中展示的路径（JSON 预览），无则仅 toast */
  previewPath?: string
  modalTitle?: string
}

export interface OrderDetailSectionCardConfig {
  key: string
  title: string
  /** 信息区字段 */
  fields?: OrderDetailFieldItem[]
  /** 区块右上角说明 */
  actions?: OrderDetailSectionActionItem[]
}

export interface OrderDetailTableColumnItem {
  title: string
  dataIndex: string
  key: string
  width?: number
  ellipsis?: boolean
  /** 单元格内展示复制图标，复制当前格展示文案 */
  copyable?: boolean
  /** 将原始值格式化为可读文案（与列表筛选项一致） */
  displayFormat?: 'shipStatus' | 'refundStatus'
}

export interface OrderDetailTableBlockConfig {
  key: string
  title: string
  /** 列表数据相对根对象的路径；若为单个对象则表格展示一行 */
  dataProp: string
  columns: OrderDetailTableColumnItem[]
}

/** 事件列表（queryOrderEventDetail）与 a-steps / 时间轴字段映射 */
export const orderDetailEventListConfig = {
  timeKey: 'eventTime',
  titleKey: 'eventName',
  descKey: 'eventDesc',
}

/** 时间轴「查看」错误详情：匹配条件 */
export type OrderDetailTimelineErrorWhen = 'server_code_not_success' | 'always'

export interface OrderDetailTimelineErrorFieldItem {
  label: string
  prop: string
}

export interface OrderDetailTimelineErrorRule {
  eventType: string
  when: OrderDetailTimelineErrorWhen
  fields: OrderDetailTimelineErrorFieldItem[]
}

/**
 * 时间轴错误信息展示规则（按顺序匹配第一条）。
 * - PLATFORM_VERIFY：仅当 serverCode 存在且不为 0000 时显示，展示 serverCode / serverMsg
 * - PAY_FAIL：始终显示，展示 clientCode / clientMsg
 */
export const orderDetailTimelineErrorViewRules: OrderDetailTimelineErrorRule[] = [
  {
    eventType: 'PLATFORM_VERIFY',
    when: 'server_code_not_success',
    fields: [
      { label: 'serverCode', prop: 'serverCode' },
      { label: 'serverMsg', prop: 'serverMsg' },
    ],
  },
  {
    eventType: 'PAY_FAIL',
    when: 'always',
    fields: [
      { label: 'clientCode', prop: 'clientCode' },
      { label: 'clientMsg', prop: 'clientMsg' },
    ],
  },
]

/** 顶部 Tag：文案来自 eventList 时间最新一条的 eventName；颜色仍取自根对象 */
export const orderDetailStatusConfig = {
  /** ant-design-vue Tag 的 color，也可后续改为自定义色板 key */
  tagColorProp: 'statusTagColor',
}

/** 顶部订单号主展示（与后端根字段一致；无则显示 —） */
export const orderDetailOrderIdsConfig = {
  /** 两排展示：左列 DBT/透传，右列 三方/原始（见 hero-ids--primary-grid） */
  primaryRows: [
    { label: 'DBT订单号', prop: 'orderInfo.orderNo', copyable: true },
    { label: '三方订单号', prop: 'orderInfo.platOrderNo', copyable: true },
    { label: '三方透传单号', prop: 'orderInfo.linkedOrderNo', copyable: true },
    // { label: '原始订单号', prop: 'orderInfo.origOrderNo', copyable: true },
  ] as OrderDetailLinkRowItem[],
}

/**
 * 摘要区：优先使用接口 orderInfo.*；当前后端常见为 productInfo 等，故增加与详情一致的快捷字段。
 * 若某路径无值，展示为 —。
 */
export const orderDetailSummaryMetricsConfig: OrderDetailSummaryMetricItem[] = [
  { label: '订单金额', prop: 'orderInfo.amount', currencyProp: 'orderInfo.currency' },
  { label: '商品名称', prop: 'orderInfo.prodName' },
  { label: '商品数量', prop: 'orderInfo.productQtyText' },
  { label: '商品类型', prop: 'orderInfo.prodType', displayFormat: 'prodType' },
  { label: '商品ID', prop: 'orderInfo.prodId', copyable: true },
  { label: '地区', prop: 'orderInfo.regionCode' },
]

export const orderDetailSectionCards: OrderDetailSectionCardConfig[] = [
  {
    key: 'orderInfo',
    title: '用户信息',
    fields: [
      { label: '用户UID', prop: 'orderInfo.userId', copyable: true },
      { label: '支付渠道', prop: 'orderInfo.payChnl', copyable: true },
      { label: '设备ID', prop: 'orderInfo.deviceId', copyable: true },
      { label: '设备型号', prop: 'orderInfo.model', copyable: true },
      { label: '应用版本', prop: 'orderInfo.appVer', copyable: true },
      { label: '操作系统', prop: 'orderInfo.os', copyable: true, displayFormat: 'os' },
    ],
  },
  {
    key: 'productInfo',
    title: '产品信息',
    fields: [
      { label: 'DBTID', prop: 'productInfo.dbtId', copyable: true },
      { label: 'AppId', prop: 'productInfo.appId', copyable: true },
      { label: '包名', prop: 'productInfo.pkg', copyable: true },
      { label: '市场', prop: 'productInfo.publishPlatformName' },
      { label: '渠道', prop: 'productInfo.channelName' },
    ],
  },
]

export const orderDetailTables: OrderDetailTableBlockConfig[] = [
  {
    key: 'reship',
    title: '复送信息',
    dataProp: 'reshipInfo',
    columns: [
      { title: '复送时间', dataIndex: 'reshipTime', key: 'reshipTime', width: 180 },
      {
        title: '复送单号',
        dataIndex: 'reshipOrderNo',
        key: 'reshipOrderNo',
        ellipsis: true,
        copyable: true,
      },
      // { title: '操作人', dataIndex: 'reshipOperatorName', key: 'reshipOperatorName' },
      {
        title: '道具状态',
        dataIndex: 'shipStatus',
        key: 'shipStatus',
        width: 120,
        displayFormat: 'shipStatus',
      },
    ],
  },
  {
    key: 'refund',
    title: '退款信息',
    dataProp: 'refundInfo',
    columns: [
      { title: '退款时间', dataIndex: 'refundTime', key: 'refundTime', width: 180 },
      {
        title: '退款单号',
        dataIndex: 'refundOrderNo',
        key: 'refundOrderNo',
        ellipsis: true,
        copyable: true,
      },
      {
        title: '退款币种',
        dataIndex: 'refundCurrency',
        key: 'refundCurrency',
      },
      {
        title: '退款金额',
        dataIndex: 'refundAmount',
        key: 'refundAmount',
      },
      {
        title: '退款渠道',
        dataIndex: 'payChnl',
        key: 'payChnl',
      },
      // { title: '操作人', dataIndex: 'refundOperatorName', key: 'refundOperatorName' },
      {
        title: '退款状态',
        dataIndex: 'refundStatus',
        key: 'refundStatus',
        width: 120,
        displayFormat: 'refundStatus',
      },
      {
        title: '退款原因',
        dataIndex: 'refundReason',
        key: 'refundReason',
        ellipsis: true,
        copyable: true,
      },
    ],
  },
  // {
  //   key: 'settlement',
  //   title: '账单信息',
  //   dataProp: 'settlementList',
  //   columns: [
  //     { title: '订单金额', dataIndex: 'orderAmount', key: 'orderAmount', width: 110 },
  //     { title: '分成比例', dataIndex: 'shareRatio', key: 'shareRatio', width: 100 },
  //     { title: '分成金额', dataIndex: 'shareAmount', key: 'shareAmount', width: 110 },
  //     { title: '账单ID', dataIndex: 'billId', key: 'billId', ellipsis: true },
  //     { title: '三方分成金额', dataIndex: 'thirdShareAmount', key: 'thirdShareAmount', width: 120 },
  //   ],
  // },
]

export function getByPath(obj: unknown, path: string): unknown {
  if (obj == null || !path) return undefined
  const keys = path.split('.')
  let cur: unknown = obj
  for (const k of keys) {
    if (cur == null || typeof cur !== 'object') return undefined
    cur = (cur as Record<string, unknown>)[k]
  }
  return cur
}

/** 毫秒时间戳（或数字字符串）格式化为本地时间 */
export function formatEventTimeMs(val: unknown): string {
  if (val === null || val === undefined || val === '') return '--'
  const n = typeof val === 'number' ? val : Number(val)
  if (!Number.isFinite(n)) return '--'
  return new Date(n).toLocaleString('zh-CN', { hour12: false })
}

export function formatFieldValue(val: unknown): string {
  if (val === null || val === undefined || val === '') return '--'
  if (typeof val === 'object') return JSON.stringify(val)
  return String(val)
}

function matchTimelineErrorRule(ev: Record<string, unknown>, rule: OrderDetailTimelineErrorRule): boolean {
  if (String(ev.eventType ?? '') !== rule.eventType) return false
  if (rule.when === 'always') return true
  if (rule.when === 'server_code_not_success') {
    const c = ev.serverCode
    if (c === undefined || c === null || c === '') return false
    return String(c) !== '0000'
  }
  return false
}

function buildTimelineErrorDetailText(
  ev: Record<string, unknown>,
  fields: OrderDetailTimelineErrorFieldItem[],
): string {
  return fields.map(({ label, prop }) => `${label}: ${formatFieldValue(ev[prop])}`).join('\n')
}

/** 时间轴单条事件：是否展示「查看」及弹层文案（无匹配规则时返回空串，不展示按钮） */
export function getTimelineErrorDetailText(ev: Record<string, unknown>): string {
  for (const rule of orderDetailTimelineErrorViewRules) {
    if (!matchTimelineErrorRule(ev, rule)) continue
    return buildTimelineErrorDetailText(ev, rule.fields)
  }
  return ''
}
